import type { ReactNode } from "react";
import { createContext, useContext, useEffect } from "react";
import type { RouterOutputs } from "@repo/api";
import { useLoadingStore } from "~/stores/use-loading-store";
import { useShallow } from "zustand/react/shallow";
import { api } from "~/lib/api";

export type Service = RouterOutputs["service"]["getAll"][number];

interface ServiceProviderProps {
  children: ReactNode;
}

interface ServiceContext {
  services: Service[];
}

const ServiceContext = createContext<ServiceContext | undefined>(undefined);

export const useService = () => {
  const context = useContext(ServiceContext);

  if (context === undefined) {
    throw new Error(
      "useService must be used within a ServiceProvider, but no provider was found"
    );
  }

  return context;
};

export const ServiceProvider = ({ children }: ServiceProviderProps) => {
  const { data: services, isFetching: isServicesLoading } =
    api.service.getAll.useQuery(undefined, {
      initialData: [],
    });

  const { setLoadingStage } = useLoadingStore(
    useShallow((state) => ({
      setLoadingStage: state.setCurrentLoadingStage,
    }))
  );

  useEffect(() => {
    if (isServicesLoading) {
      setLoadingStage("services");
    } else {
      setLoadingStage(undefined);
    }
  }, [isServicesLoading, setLoadingStage]);

  const context = {
    services,
  };

  return (
    <ServiceContext.Provider value={context}>
      {children}
    </ServiceContext.Provider>
  );
};
